import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import { LangSwitcher } from "./kv";

export const ConsentModal = () => {
  const { t } = useT();
  const { setUser, logout } = useAuth();
  const [kvkk, setKvkk] = useState(false);
  const [cookies, setCookies] = useState(false);
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/consent", { kvkk, cookies });
      setUser(data);
      toast.success(t("consent.accepted"));
    } catch (e) {
      toast.error(errMsg(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#2c1810]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="consent-modal">
      <div className="bg-[#FAF6F0] w-full sm:max-w-2xl max-h-[92vh] flex flex-col rounded-t-3xl sm:rounded-3xl shadow-2xl border border-[#E8DFD0] kv-rise">
        <div className="p-5 sm:p-7 border-b border-[#E8DFD0] flex items-start justify-between gap-3">
          <div className="flex gap-3">
            <ShieldCheck className="h-7 w-7 text-[#46634c] shrink-0" />
            <h2 className="font-display text-2xl font-bold leading-tight">{t("consent.title")}</h2>
          </div>
          <LangSwitcher />
        </div>
        <div className="overflow-y-auto px-5 sm:px-7 py-5 space-y-4 text-sm leading-relaxed text-stone-700" data-testid="consent-text">
          <p>{t("consent.intro")}</p>
          {["p1", "p2", "p3", "p4"].map((p) => (
            <div key={p}>
              <h3 className="font-semibold text-[#2c1810] mb-1">{t(`consent.${p}_t`)}</h3>
              <p>{t(`consent.${p}`)}</p>
            </div>
          ))}
        </div>
        <div className="p-5 sm:p-7 border-t border-[#E8DFD0] space-y-3 bg-white/60 rounded-b-3xl">
          <label className="flex gap-3 items-start text-sm cursor-pointer">
            <Checkbox checked={kvkk} onCheckedChange={(v) => setKvkk(!!v)} data-testid="consent-kvkk-checkbox" className="mt-0.5" />
            <span>{t("consent.check_kvkk")}</span>
          </label>
          <label className="flex gap-3 items-start text-sm cursor-pointer">
            <Checkbox checked={cookies} onCheckedChange={(v) => setCookies(!!v)} data-testid="consent-cookie-checkbox" className="mt-0.5" />
            <span>{t("consent.check_cookies")}</span>
          </label>
          <p className="text-xs text-stone-500">{t("consent.legal_note")}</p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
            <Button variant="ghost" onClick={logout} data-testid="consent-logout-button">{t("common.logout")}</Button>
            <Button disabled={!kvkk || !cookies || busy} onClick={accept} data-testid="consent-accept-button" className="h-11 rounded-full px-6">
              {t("consent.accept")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
